/**
 * CompanySelectModal - Allows user to select from matching public companies
 * or indicate the company is private with optional metadata
 */

import { useState } from 'react'
import { PrivateCompanyMetadata } from '../../types'

export interface CompanyMatch {
  ticker: string
  name: string
  sector?: string
  exchange?: string
  quoteType?: string
}

// Re-export for convenience
export type { PrivateCompanyMetadata }

interface CompanySelectModalProps {
  searchQuery: string
  matches: CompanyMatch[]
  onSelectPublic: (match: CompanyMatch) => void
  onSelectPrivate: (metadata: PrivateCompanyMetadata) => void
  onCancel: () => void
  isSearching?: boolean
}

const SIZE_OPTIONS = [
  'Unknown',
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-500 employees',
  '501-1000 employees',
  '1001-5000 employees',
  '5001-10000 employees',
  '10000+ employees',
]

const REVENUE_OPTIONS = [
  'Unknown',
  'Pre-revenue',
  '<$1M',
  '$1M-$10M',
  '$10M-$50M',
  '$50M-$100M',
  '$100M-$500M',
  '$500M-$1B',
  '$1B-$10B',
  '$10B+',
]

const SECTOR_OPTIONS = [
  'Unknown',
  'Technology',
  'Healthcare',
  'Financial Services',
  'Consumer Goods',
  'Consumer Services',
  'Industrials',
  'Energy',
  'Materials',
  'Utilities',
  'Real Estate',
  'Communications',
  'Other',
]

export function CompanySelectModal({
  searchQuery,
  matches,
  onSelectPublic,
  onSelectPrivate,
  onCancel,
  isSearching = false,
}: CompanySelectModalProps) {
  const [showPrivateForm, setShowPrivateForm] = useState(false)
  const [metadata, setMetadata] = useState<PrivateCompanyMetadata>({})

  const handlePrivateSubmit = () => {
    onSelectPrivate(metadata)
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-term-bg border border-term-border rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-term-border">
          <h2 className="text-lg font-semibold text-term-primary">
            {showPrivateForm ? '🔒 Private Company Details' : `🔍 Results for "${searchQuery}"`}
          </h2>
          <p className="text-sm text-term-textDim mt-1">
            {showPrivateForm
              ? 'Provide optional details to improve research quality'
              : 'Select a public company or indicate this is a private company'}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {isSearching ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-term-textDim">
                <span className="animate-pulse">Searching...</span>
              </div>
            </div>
          ) : showPrivateForm ? (
            /* Private Company Form */
            <div className="space-y-4">
              <div className="bg-term-info/10 border border-term-info/30 rounded p-3 text-sm">
                <span className="text-term-info font-medium">💡 All fields are optional</span>
                <p className="text-term-textDim mt-1">
                  Adding details helps our AI conduct more targeted research via LinkedIn,
                  news sources, and public records.
                </p>
              </div>

              {/* HQ Location */}
              <div>
                <label className="block text-sm font-medium text-term-text mb-1">
                  HQ Location
                </label>
                <input
                  type="text"
                  placeholder="e.g., San Francisco, CA or London, UK"
                  value={metadata.hqLocation || ''}
                  onChange={(e) => setMetadata({ ...metadata, hqLocation: e.target.value })}
                  className="term-input w-full"
                />
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-term-text mb-1">
                  Company Website
                </label>
                <input
                  type="text"
                  placeholder="e.g., https://company.com"
                  value={metadata.website || ''}
                  onChange={(e) => setMetadata({ ...metadata, website: e.target.value })}
                  className="term-input w-full"
                />
              </div>

              {/* LinkedIn URL */}
              <div>
                <label className="block text-sm font-medium text-term-text mb-1">
                  LinkedIn Company Page
                </label>
                <input
                  type="text"
                  placeholder="e.g., https://linkedin.com/company/company-name"
                  value={metadata.linkedinUrl || ''}
                  onChange={(e) => setMetadata({ ...metadata, linkedinUrl: e.target.value })}
                  className="term-input w-full"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Sector */}
                <div>
                  <label className="block text-sm font-medium text-term-text mb-1">
                    Sector
                  </label>
                  <select
                    value={metadata.sector || 'Unknown'}
                    onChange={(e) => setMetadata({ ...metadata, sector: e.target.value })}
                    className="term-input w-full"
                  >
                    {SECTOR_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Estimated Size */}
                <div>
                  <label className="block text-sm font-medium text-term-text mb-1">
                    Estimated Size
                  </label>
                  <select
                    value={metadata.estimatedSize || 'Unknown'}
                    onChange={(e) => setMetadata({ ...metadata, estimatedSize: e.target.value })}
                    className="term-input w-full"
                  >
                    {SIZE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Estimated Revenue */}
                <div>
                  <label className="block text-sm font-medium text-term-text mb-1">
                    Estimated Revenue
                  </label>
                  <select
                    value={metadata.estimatedRevenue || 'Unknown'}
                    onChange={(e) => setMetadata({ ...metadata, estimatedRevenue: e.target.value })}
                    className="term-input w-full"
                  >
                    {REVENUE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ) : (
            /* Public Company Selection */
            <div className="space-y-2">
              {matches.length > 0 ? (
                <>
                  <p className="text-sm text-term-textDim mb-3">
                    Found {matches.length} public {matches.length === 1 ? 'company' : 'companies'}:
                  </p>
                  {matches.map((match) => (
                    <button
                      key={match.ticker}
                      onClick={() => onSelectPublic(match)}
                      className="w-full text-left p-3 rounded border border-term-border hover:border-term-primary hover:bg-term-primary/5 transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-mono text-term-primary font-bold">
                            {match.ticker}
                          </span>
                          <span className="text-term-text ml-2">{match.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {match.exchange && (
                            <span className="text-xs text-term-textDim px-2 py-0.5 bg-term-bgDark rounded">
                              {match.exchange}
                            </span>
                          )}
                          {match.quoteType && match.quoteType !== 'EQUITY' && (
                            <span className="text-xs text-term-warning px-2 py-0.5 bg-term-warning/10 rounded">
                              {match.quoteType}
                            </span>
                          )}
                          <span className="text-term-textDim group-hover:text-term-primary">
                            →
                          </span>
                        </div>
                      </div>
                      {match.sector && (
                        <div className="text-xs text-term-textDim mt-1">
                          {match.sector}
                        </div>
                      )}
                    </button>
                  ))}
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-term-textDim">
                    No public companies found matching "{searchQuery}"
                  </p>
                  <p className="text-sm text-term-textDim mt-2">
                    This appears to be a private company.
                  </p>
                </div>
              )}

              {/* Private Company Option */}
              <div className="border-t border-term-border pt-4 mt-4">
                <button
                  onClick={() => setShowPrivateForm(true)}
                  className="w-full text-left p-3 rounded border border-term-warning/30 hover:border-term-warning hover:bg-term-warning/5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🔒</span>
                    <div>
                      <div className="font-medium text-term-warning">
                        This is a private company
                      </div>
                      <div className="text-xs text-term-textDim">
                        Research via web sources, LinkedIn, news, and public records
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-term-border flex justify-end gap-3">
          {showPrivateForm ? (
            <>
              <button
                onClick={() => setShowPrivateForm(false)}
                className="term-button"
              >
                ← Back
              </button>
              <button
                onClick={handlePrivateSubmit}
                className="term-button term-button-primary"
              >
                Start Research
              </button>
            </>
          ) : (
            <button onClick={onCancel} className="term-button">
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
